/**
 * Trick — the current center pile. Owner: Phase-3 A3.2.
 *
 * Renders cards in seat-relative slot positions:
 *   layoutSlot = ((seat - view.you) + n) % n
 * matches Felt's slot convention (slot 0 bottom; 4p: slot 1 right, slot 2 top,
 * slot 3 left; 6p: 1 BR, 2 TR, 3 top, 4 TL, 5 BL).
 *
 * v1.4 Phase 20.6 — TRICK-END ANIMATION FLICKER ELIMINATION (2026-05-22).
 *
 * Phase 20.5 fixed the 11-element DOM accumulation and got all 4 cards
 * animating, but a per-frame rAF stress-test exposed 4 remaining flicker
 * sources:
 *
 *   1. The parent `.trick` re-key (`snap-${seq}` ↔ "live") tore down the
 *      ENTIRE subtree on every snapshot start. The 3 already-mounted live
 *      cards unmounted and re-mounted with the snapshot, producing one
 *      frame of churn at the boundary (~3 cards vanished, ~4 reappeared
 *      with possibly different DOM identity).
 *   2. The dissolve transition had a per-card `delay: index * 40 ms`
 *      stagger but the drop timer only waited `DISSOLVE_MS` (200 ms) — so
 *      the LAST card (index 3, delay 120 ms) had only 80 ms to fade from
 *      opacity 0.92 to 0 before the snapshot was dropped. Captured frame
 *      data showed the last card popping out at opacity 0.53 (still half
 *      visible) when the DOM cleared. Headline visible flicker.
 *   3. The winner's `scale: [1, 1.08, 1]` settling pulse interrupted
 *      mid-keyframe when the phase changed to collecting (settle duration
 *      = pulse duration = 200 ms, so the pulse barely completes; with
 *      ease-out the scale was 1.06 - 1.08 at phase-change time, NOT 1.0).
 *      Motion then had to interrupt the pulse and tween from the
 *      mid-pulse value to the collect target — visible scale stutter on
 *      the winner card.
 *   4. The live-mode branch had no `.trick-play-inner` wrapper, while the
 *      snapshot branch did. The DOM structure differed between modes, so
 *      even without the parent re-key, transitioning a card from live to
 *      snapshot would add/remove the inner div.
 *
 * Phase 20.6 fixes all four:
 *
 *   - Single render path. Both live AND snapshot use the same nested
 *     `<m.div .trick-play>` → `<m.div .trick-play-inner>` → `<Card>`
 *     structure. The inner m.div's `animate` prop varies by mode/phase;
 *     the DOM nodes are stable.
 *   - NO parent re-key. The `.trick` container's React key is constant.
 *     Cards mount and unmount via their own stable `cardKey` keys. The 3
 *     cards already in the live trick when the 4th lands KEEP their DOM
 *     nodes when the snapshot kicks in — only the 4th mounts new.
 *   - Dissolve has NO stagger (`delay: 0` for all cards). All 4 cards
 *     fade from opacity 0.92 → 0 simultaneously over DISSOLVE_MS, and the
 *     drop timer fires EXACTLY when all are at opacity 0. No card pops
 *     out mid-fade.
 *   - Winner pulse REMOVED. The "this is the winner" cue is delivered
 *     visually by (a) the `.trick-play-winning` CSS class's gold drop-
 *     shadow filter, and (b) the seat ring's gold pulse (Seat.tsx's
 *     `.seat-trick-winner` keyframes animation, applied for 1.2 s on the
 *     winner seat). Both run independently of the trick-card layout, so
 *     no animation conflict.
 *   - Live cards now also get an `animate` prop (resting at {x:0, y:0,
 *     scale:1, rotate:0, opacity:1}) so Motion has tween state ready when
 *     the same DOM node transitions into snapshot mode — no fresh-mount
 *     "instant commit" surprise.
 *
 * Visual timeline (~1.07 s total):
 *   Phase A — Settle (200 ms): 4 cards in slot positions. Winner has the
 *             gold drop-shadow filter (and the seat ring is pulsing gold).
 *   Phase B — Collecting (550 ms + 40 ms per-card stagger = 670 ms): every
 *             card's inner translates (dx, dy) toward the winner's seat
 *             ring center, scales 0.65, rotates ±6° toward direction of
 *             travel, opacity 0.92. cubic-bezier(0.4, 0, 0.2, 1).
 *   Phase C — Dissolved (200 ms, NO stagger): cards fade opacity 0.92 → 0
 *             in unison at the winner.
 *   Phase D — Drop: snapshot cleared. Felt is empty until the next leader
 *             plays (server INTERTRICK_MS = 1800 ms ≈ 730 ms of intentional
 *             dead air, matching premium card-game UX).
 *
 * useReducedMotion: skip the spatial animation; cards stay at slot
 * positions during settling, then fade in unison over 200 ms.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { m, useReducedMotion } from "motion/react";
import type { GameEventWire, Suit, TrickPlay } from "@hookum/protocol";
import { useRecentEvents, useView } from "../state/hooks.js";
import { Card } from "./Card.js";

/** Phase A — settle: cards in slot positions. Owner-tuned 2026-05-22: a
 * full 2 s linger so players can READ the completed trick before the
 * fly-to-winner animation begins. The server's INTERTRICK_MS is bumped to
 * match (apps/server/src/state.ts) so the bots don't start the next trick
 * during the linger. */
const SETTLE_MS = 2000;
/** Phase B — collection flight: cards translate to winner's seat. */
const COLLECT_MS = 550;
/** Phase C — dissolve at winner: opacity → 0. NO stagger. */
const DISSOLVE_MS = 200;
/** Per-card stagger inside Phase B only (NOT Phase C). */
const STAGGER_MS = 40;

type AnimPhase = "settling" | "collecting" | "dissolved";

interface Snapshot {
  plays: ReadonlyArray<TrickPlay>;
  winnerSeat: number;
  winnerTeam: "A" | "B";
  /** Sequence id of the trickWon event that triggered this snapshot. */
  seq: number;
  phase: AnimPhase;
}

interface Delta {
  dx: number;
  dy: number;
}

function findLatestTrickWon(
  events: ReadonlyArray<{ readonly seq: number; readonly event: GameEventWire }>,
): { seq: number; seat: number; team: "A" | "B" } | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e && e.event.type === "trickWon") {
      return { seq: e.seq, seat: e.event.seat, team: e.event.team };
    }
  }
  return null;
}

function cardIdsKey(plays: ReadonlyArray<TrickPlay>): string {
  return plays.map((p) => p.card.id).join(",");
}

/**
 * Compute (dx, dy) from each `.trick-play[data-card-key="…"]` element inside
 * the snapshot root to the winner's seat-ring center. First-write-wins so
 * a stray live-mode element that briefly overlaps cannot overwrite the
 * snapshot's own measurement.
 *
 * Falls back to a generic upward float if the seat ring isn't measurable
 * (e.g., responsive CSS removes the avatar at narrow widths).
 */
function measureCollectionTarget(
  rootEl: HTMLDivElement,
  winnerSeatIndex: number,
): Map<string, Delta> {
  const map = new Map<string, Delta>();
  const seatEl = document.querySelector<HTMLElement>(
    `.seat[data-seat="${winnerSeatIndex}"] .seat-ring`,
  );
  const seatRect = seatEl?.getBoundingClientRect();
  const visible =
    seatRect !== undefined && seatRect.width > 0 && seatRect.height > 0;
  const targetX = visible ? seatRect.left + seatRect.width / 2 : null;
  const targetY = visible ? seatRect.top + seatRect.height / 2 : null;
  const fallbackDy = -160;

  const plays = rootEl.querySelectorAll<HTMLElement>(
    ".trick-play[data-card-key]",
  );
  for (const el of plays) {
    const key = el.getAttribute("data-card-key");
    if (!key) continue;
    if (map.has(key)) continue;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    if (targetX !== null && targetY !== null) {
      map.set(key, { dx: targetX - cx, dy: targetY - cy });
    } else {
      map.set(key, { dx: 0, dy: fallbackDy });
    }
  }
  return map;
}

/**
 * Visible tilt toward direction of travel. atan2 → degrees → divide by 6
 * (was 12 pre-Phase-20.5 — too subtle to see) → clamp ±6°.
 */
function computeRotation(dx: number, dy: number): number {
  const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  const tilt = Math.max(-6, Math.min(6, angleDeg / 6));
  return tilt;
}

/** Rest state used as the live-mode animate target AND the snapshot
 * settling-phase animate target. Provides Motion with explicit tween
 * values for every card from mount, so phase transitions interpolate
 * smoothly instead of snap-committing. */
const REST_ANIMATE = { x: 0, y: 0, scale: 1, rotate: 0, opacity: 1 } as const;

export function Trick(): JSX.Element | null {
  const view = useView();
  const events = useRecentEvents();
  const prefersReducedMotion = useReducedMotion();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [target, setTarget] = useState<Map<string, Delta> | null>(null);
  const lastHandledSeqRef = useRef<number>(-1);
  /** Cards-tuple (cardId join) of the last `view.lastTrick.plays` we acted
   * on. The store dispatches `event` and `state` messages as SEPARATE actions
   * (see state/store.ts applyServer). The server broadcasts events FIRST
   * then state (apps/server/src/room.ts `broadcastEvents(...); broadcastState();`),
   * so the client RELIABLY receives trickWon BEFORE the matching state push
   * with the updated `view.lastTrick`. If we capture `view.lastTrick.plays`
   * naively when the event fires, we get STALE cards from the PREVIOUS
   * trick — visible to the user as the snapshot animating the wrong 4 cards
   * (the most disturbing "flickering" bug because the cards CHANGE IDENTITY
   * at the moment of the snapshot start).
   *
   * Wait until `view.lastTrick.plays` has cards DIFFERENT from the last
   * trick we animated. Reference identity doesn't work because zod parses a
   * NEW `lastTrick` object on every state push, even when the underlying
   * data is unchanged. Value identity (cardIds tuple) is the correct guard. */
  const lastActedTrickKeyRef = useRef<string>("");
  /** Cards-tuple of the most-recently animated snapshot. Used to suppress the
   * reconnect-fallback linger from showing the just-animated trick again. */
  const animatedPlaysKeyRef = useRef<string>("");
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Effect A — detect a NEW trickWon event in the recent-events tail and
  // arm a snapshot from `view.lastTrick.plays`. The seq guard ensures we
  // only act on each trickWon once. The cardIds-tuple guard ensures we
  // wait for the state push that updates `view.lastTrick` to match the
  // just-fired trickWon, avoiding the event-first race condition.
  useEffect(() => {
    const latest = findLatestTrickWon(events);
    if (!latest) return;
    if (latest.seq <= lastHandledSeqRef.current) return;
    const lt = view?.lastTrick;
    if (!lt) return;
    const plays = lt.plays ?? [];
    if (plays.length === 0) return;
    const playsKey = cardIdsKey(plays);
    if (playsKey === lastActedTrickKeyRef.current) {
      // `view.lastTrick` is still the PREVIOUS resolved trick — the state
      // push for THIS trick hasn't landed yet. Bail and wait.
      return;
    }
    lastActedTrickKeyRef.current = playsKey;
    lastHandledSeqRef.current = latest.seq;
    setSnapshot({
      plays,
      winnerSeat: latest.seat,
      winnerTeam: latest.team,
      seq: latest.seq,
      phase: "settling",
    });
    setTarget(null);
  }, [events, view?.lastTrick]);

  // Effect B — phase timeline keyed on snapshot.seq.
  //
  // Phase B/C timing accounts for the collect stagger but NOT a dissolve
  // stagger (dissolve has no stagger in Phase 20.6, see header).
  useEffect(() => {
    if (!snapshot) return;
    const seq = snapshot.seq;
    const winnerSeat = snapshot.winnerSeat;
    const playCount = snapshot.plays.length;
    const playsKey = cardIdsKey(snapshot.plays);

    if (prefersReducedMotion) {
      const drop = window.setTimeout(() => {
        animatedPlaysKeyRef.current = playsKey;
        setSnapshot((c) => (c && c.seq === seq ? null : c));
        setTarget(null);
      }, 250);
      return () => window.clearTimeout(drop);
    }

    const settle = window.setTimeout(() => {
      const root = rootRef.current;
      const measured = root ? measureCollectionTarget(root, winnerSeat) : null;
      setTarget(measured);
      setSnapshot((c) =>
        c && c.seq === seq ? { ...c, phase: "collecting" } : c,
      );
    }, SETTLE_MS);

    // Collecting takes COLLECT_MS + per-card stagger; the LAST card finishes
    // at SETTLE_MS + COLLECT_MS + (playCount - 1) * STAGGER_MS.
    const lastCardArrival = COLLECT_MS + Math.max(0, playCount - 1) * STAGGER_MS;
    const dissolve = window.setTimeout(() => {
      setSnapshot((c) =>
        c && c.seq === seq ? { ...c, phase: "dissolved" } : c,
      );
    }, SETTLE_MS + lastCardArrival);

    // Dissolve has NO stagger — all cards fade in unison over DISSOLVE_MS.
    // The drop timer fires exactly when the dissolve finishes.
    const drop = window.setTimeout(() => {
      animatedPlaysKeyRef.current = playsKey;
      setSnapshot((c) => (c && c.seq === seq ? null : c));
      setTarget(null);
    }, SETTLE_MS + lastCardArrival + DISSOLVE_MS);

    return () => {
      window.clearTimeout(settle);
      window.clearTimeout(dissolve);
      window.clearTimeout(drop);
    };
  }, [
    snapshot?.seq,
    snapshot?.winnerSeat,
    snapshot?.plays,
    prefersReducedMotion,
  ]);

  // Effect C — drop any in-flight snapshot when leaving the playing phase.
  useEffect(() => {
    if (view && view.phase !== "playing" && snapshot) {
      setSnapshot(null);
      setTarget(null);
    }
  }, [view, snapshot]);

  const playerCount = (view?.config.players ?? 4) as 4 | 6;
  const you = view?.you ?? 0;
  const layoutSlot = useMemo(
    () =>
      (seat: number): number =>
        ((seat - you) % playerCount + playerCount) % playerCount,
    [you, playerCount],
  );

  if (!view) return null;

  // Source resolution. Snapshot has priority; otherwise live trick plays;
  // otherwise reconnect-fallback linger (lastTrick + nextTrickAt set), with
  // the linger SUPPRESSED if we just animated those same plays.
  let renderPlays: ReadonlyArray<TrickPlay>;
  let mode: "snapshot" | "live";
  if (snapshot) {
    renderPlays = snapshot.plays;
    mode = "snapshot";
  } else {
    const livePlays = view.trick?.plays ?? [];
    if (livePlays.length > 0) {
      renderPlays = livePlays;
      mode = "live";
    } else if (
      view.lastTrick &&
      view.lastTrick.plays.length > 0 &&
      view.nextTrickAt !== null &&
      cardIdsKey(view.lastTrick.plays) !== animatedPlaysKeyRef.current
    ) {
      renderPlays = view.lastTrick.plays;
      mode = "live";
    } else {
      return null;
    }
  }

  const animPhase: AnimPhase | "idle" = snapshot ? snapshot.phase : "idle";
  const ledSuit: Suit | null =
    mode === "snapshot"
      ? null
      : (view.trick?.plays.length ?? 0) > 0
        ? view.trick?.ledSuit ?? null
        : view.lastTrick?.ledSuit ?? null;

  return (
    <div
      ref={rootRef}
      // NO parent re-key. The .trick container's React identity stays
      // constant across live ↔ snapshot transitions. Stable cardKey React
      // keys on each child mean cards from the live trick KEEP their DOM
      // nodes when the snapshot kicks in — only newly-added cards mount.
      className={
        "trick" +
        (snapshot ? " trick-collecting trick-collecting-" + animPhase : "")
      }
      role="group"
      aria-label="Current hand"
      data-led-suit={ledSuit ?? undefined}
      data-trick-phase={animPhase}
    >
      {renderPlays.map((play, index) => {
        const slot = layoutSlot(play.seat);
        const cardKey = play.card.id;
        const isWinner =
          mode === "snapshot" &&
          snapshot !== null &&
          play.seat === snapshot.winnerSeat;

        // Unified animate target. Live mode and snapshot/settling both use
        // REST_ANIMATE. Snapshot/collecting and snapshot/dissolved set
        // fly-to-winner translation/scale/rotate, with opacity 0.92 →
        // (collecting) or 0 (dissolved).
        const delta =
          mode === "snapshot" ? target?.get(cardKey) ?? null : null;
        const dx = delta?.dx ?? 0;
        const dy = delta?.dy ?? 0;
        const rotation =
          delta && !prefersReducedMotion ? computeRotation(dx, dy) : 0;

        let animateTo: Record<string, number | string>;
        let transitionDuration: number;
        let transitionDelay: number;

        if (mode === "live" || animPhase === "settling") {
          // Cards rest at slot position.
          animateTo = REST_ANIMATE as unknown as Record<string, number>;
          transitionDuration = mode === "live" ? 0 : SETTLE_MS / 1000;
          transitionDelay = 0;
        } else if (animPhase === "collecting") {
          if (prefersReducedMotion) {
            animateTo = { ...REST_ANIMATE };
          } else {
            animateTo = {
              x: dx,
              y: dy,
              scale: 0.65,
              rotate: rotation,
              opacity: 0.92,
            };
          }
          transitionDuration = COLLECT_MS / 1000;
          transitionDelay = prefersReducedMotion
            ? 0
            : (index * STAGGER_MS) / 1000;
        } else {
          // animPhase === "dissolved" — opacity-only change from collecting
          // end state. NO STAGGER so all cards fade in unison.
          if (prefersReducedMotion) {
            animateTo = { x: 0, y: 0, scale: 1, rotate: 0, opacity: 0 };
          } else {
            animateTo = {
              x: dx,
              y: dy,
              scale: 0.65,
              rotate: rotation,
              opacity: 0,
            };
          }
          transitionDuration = DISSOLVE_MS / 1000;
          transitionDelay = 0;
        }

        return (
          <m.div
            key={cardKey}
            className={
              "trick-play trick-play-slot-" +
              slot +
              (isWinner ? " trick-play-winning" : "")
            }
            data-seat={play.seat}
            data-layout-slot={slot}
            data-card-key={cardKey}
          >
            <m.div
              className="trick-play-inner"
              initial={REST_ANIMATE}
              animate={animateTo}
              transition={{
                duration: transitionDuration,
                delay: transitionDelay,
                ease: [0.4, 0, 0.2, 1] as const,
              }}
            >
              <Card card={play.card} />
            </m.div>
          </m.div>
        );
      })}
    </div>
  );
}
